import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum EightSleepPOBilingualLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemName = 'itemName', // name of the item, often SKU, Unique ID, reference ID, etc
  name = 'name', // descriptive name of the item
  description = 'description', // verbose description of the item (human parseable)
  itemQty = 'itemQty', // quantity of the item for order
  unitPrice = 'unitPrice', // price per unit
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
}

/**
 * Every row of a party block is a Chinese value with its English counterpart underneath, so the text
 * columns come in `x` / `xEn` pairs and the renderer stacks them in one cell.
 *
 * Only the rows whose text actually differs by language get a pair. Zip code, phone number and
 * email address are digits and addresses, identical in both, so they stay single and render once.
 */
export const EightSleepPOBilingualAddressInfoSchema = yup.object().shape({
  attn: yup.string().nullable().optional(),
  attnEn: yup.string().nullable().optional(),
  companyName: yup.string().nullable().optional(),
  companyNameEn: yup.string().nullable().optional(),
  addressLine1: yup.string().nullable().optional(),
  addressLine1En: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  addressLine2En: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  cityEn: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  stateEn: yup.string().nullable().optional(),
  zipCode: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
  countryEn: yup.string().nullable().optional(),
  phoneNumber: yup.string().nullable().optional(),
  emailAddress: yup.string().nullable().optional(),
  displayMultipleLines: yup.string().nullable().optional(),
})
export type EightSleepPOBilingualFileAddressData = yup.InferType<
  typeof EightSleepPOBilingualAddressInfoSchema
>

export const EightSleepPOBilingualFileDataSchema = yup.object().shape({
  /**
   * Legal entity name rendered under the logo, beneath a bilingual "Company Name / 公司名称"
   * caption. Holds the Chinese name; corporationNameEn renders under it.
   */
  corporationName: yup.string().nullable().optional(),
  /** English legal entity name, rendered directly under corporationName with no caption. */
  corporationNameEn: yup.string().nullable().optional(),
  issuer: EightSleepPOBilingualAddressInfoSchema,
  orderNumber: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  dateReady: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  destination: EightSleepPOBilingualAddressInfoSchema,
  vendor: EightSleepPOBilingualAddressInfoSchema,
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        name: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .required(),
  items2: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        name: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .optional(),
  dontRoundUnitPrice: yup.string().nullable().optional(),
  agreement: yup.string().nullable().optional(),
  notes: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
  useLocaleQuantityFormatting: yup.string().nullable().optional(),
  // Approval block rendered under Notes & Instructions. The buyer-side three carry names; the
  // seller-side three are normally blank so there is room to sign and stamp the printed page.
  approvalPurchase: yup.string().nullable().optional(),
  approvalAudit: yup.string().nullable().optional(),
  approvalApprove: yup.string().nullable().optional(),
  approvalSeller: yup.string().nullable().optional(),
  approvalSignatureSeal: yup.string().nullable().optional(),
  approvalSignedBackDate: yup.string().nullable().optional(),
})

export type EightSleepPOBilingualFileData = yup.InferType<
  typeof EightSleepPOBilingualFileDataSchema
>
type RequiredFileData = MakeRequiredAndNullable<EightSleepPOBilingualFileData>

/**
 * These UUIDs become `GeneratedFileField.id` primary keys, so they must be unique across every
 * template — they are deliberately fresh rather than copied from `EightSleepPOFileMappings` or
 * `EightSleepPORmbFileMappings`. Reusing one would make `resetGeneratedFileFields` (which looks
 * rows up filtered by file type) miss the existing row, take the create path, and fail on a
 * primary-key conflict.
 */
export const EightSleepPOBilingualFileMappings: RequiredFileData = {
  corporationName: 'f7d45c9c-348a-4b36-9993-34214d25c900',
  corporationNameEn: '569efcd4-8452-4871-b1dd-96bd66ad611a',
  issuer: {
    attn: 'c4b0d297-e6f2-4f4a-bbcc-6d0319fb0efd',
    attnEn: 'f7451d5f-7eea-465d-a105-7d902345b7fe',
    companyName: 'd9c0af78-afcb-43c8-bc40-b9d0410be89c',
    companyNameEn: '1c1a4e6c-9f8b-4b1e-8f8a-2b8c2f6b8b4a',
    addressLine1: '1192acba-f24f-4192-b58d-2d7c5650a7c0',
    addressLine1En: '0bd441ef-6220-4a74-86b6-e6b05004e77d',
    addressLine2: 'b2da59ec-14a7-4f0f-ae58-c579a30c1ae4',
    addressLine2En: 'a2d7e259-4566-4971-b970-0a461e8c1e83',
    city: 'f464aa9d-8202-4e97-8722-4f4e301001a3',
    cityEn: 'eaf09b72-ac25-4ad5-89a0-bc3632f6d368',
    state: '6900c94d-50f4-475a-ba50-ebf8adeabef9',
    stateEn: 'ea1a3e75-39d3-441d-848f-737a6da6dbd5',
    zipCode: '477181e0-a965-4f84-9b29-5045a2fb474e',
    country: 'faed31ee-6d42-4964-bca0-788abe4e1082',
    countryEn: '8a30d8ce-aea4-43bf-95ed-ddd2b1937f10',
    phoneNumber: 'f6480594-d787-4b6a-946b-d138cf3a9eda',
    emailAddress: '3d5a5ede-3c3b-4143-99f9-532c0d7b0986',
    displayMultipleLines: 'f2c5c829-60dc-4813-ac75-e05092352bcf',
  },
  orderNumber: '9d46aa3c-4778-4062-b069-18740acd62f1',
  paymentTerms: '4c8ded5c-5950-4511-a125-e6691aacd811',
  freightTerms: '5c92ee07-01f6-4acc-9664-4f91478871c7',
  dateIssued: '34db46f7-0912-49bf-87f5-4a8ac721248d',
  dateReady: 'c94702fe-057d-4d38-9e4f-ad23e1af573b',
  destination: {
    attn: '9007a055-e978-463a-9030-4d2fcafc24e2',
    attnEn: 'fd519732-31dd-4d8f-b66e-7e26b15d6696',
    companyName: 'aaa6cac4-d768-4301-9d46-2c5b1f602959',
    companyNameEn: 'd3a2f9e0-6c4b-4e2a-9d1f-7a5b3c8e0f2d',
    addressLine1: '3843c6db-a9db-4e97-a19e-5764930801dd',
    addressLine1En: '9c370879-a85b-496d-a62a-2bedb12e9410',
    addressLine2: 'eeb865ed-eed6-447e-90d7-89855c2dc63d',
    addressLine2En: '398c9524-40ef-4404-a101-2b4866918c10',
    city: '53fbad63-1363-4cfe-bf58-e1d330b75995',
    cityEn: 'a0932a83-a1cd-4479-bd2b-acb88accbfee',
    state: 'a5a2ec98-e020-4a1b-938b-d763d21d0553',
    stateEn: '067a57e4-999e-473a-86e4-3d612cbe7273',
    zipCode: '143a4efd-052d-46b0-8af8-adda2ef54599',
    country: '23c6fd5c-1baf-49d2-b6d0-153ceb48e568',
    countryEn: '18af6d40-6519-4968-968a-d33c232399c3',
    phoneNumber: '3168bec3-206f-4647-87b3-c86d1b977040',
    emailAddress: 'b91e6e94-0461-442c-9405-df30d17e8151',
    displayMultipleLines: 'dd784b7e-738b-408d-8083-8cd5a4b158e3',
  },
  vendor: {
    attn: '6474066e-138b-4881-8efa-bfbe34695dde',
    attnEn: '1f469966-faab-469b-9067-80ec7e8833b7',
    companyName: '8a5de6ca-fe0c-423e-ae50-7bad4869558d',
    companyNameEn: '96250a78-7b24-482a-a106-a673f3541357',
    addressLine1: '59923e4e-7395-4242-beca-c1bd8dff1e4f',
    addressLine1En: '6caddaae-2770-488b-8d65-6754889ba317',
    addressLine2: 'dd7720fc-4881-4ba7-95b2-975eba3b4673',
    addressLine2En: 'e85f5356-ac7d-4479-a8d3-b80c20671714',
    city: 'f2ef16cb-9924-4289-b5a4-78f24611294b',
    cityEn: '8fda9c5c-219b-4e17-8391-1343c90ea394',
    state: 'd67f5b41-a71f-4d6e-976e-495e5ca73d76',
    stateEn: 'ac768713-343b-471d-8044-84d1cd4aa19b',
    zipCode: '1e8bad47-047e-43d9-adaa-dcf2a7a11848',
    country: '270de477-2ac2-4ad1-99cc-736bdaf2af88',
    countryEn: '7dcec3f3-6071-403c-be98-3e946197eb46',
    phoneNumber: 'bf6839f3-a8cc-4086-901a-e25be5778fe3',
    emailAddress: 'c8b924f7-efc4-4c2c-b380-ac6b88cdc002',
    displayMultipleLines: '5208e68a-8765-4dab-a273-544959cf8b8b',
  },
  items: [
    {
      itemNumber: null,
      itemName: 'ac83d114-a786-48f8-8106-a6d19599cbeb',
      name: '48322064-f548-4096-97b3-8f51516fa936',
      description: '98a781a7-8eb2-49c5-b261-1f093213ccd7',
      itemQty: '921dba50-d34c-4501-a817-ca00d8e1b1c5',
      unitPrice: '4b067dcd-dba5-4433-81d1-22c4980befe5',
      lineTotal: null,
    },
  ],
  items2: [
    {
      itemNumber: null,
      itemName: 'bbe08ba7-1eed-4922-97ac-a23d5b878f64',
      name: '726c087a-86a2-4b5c-ae3f-2dd40ebc577f',
      description: '2e344278-4d96-4d89-95ec-05fb15e0a01f',
      itemQty: '0720f4dc-606b-44e2-b403-a944e6f0f3d5',
      unitPrice: '613134a7-17d3-4150-817f-393d07b4b1d5',
      lineTotal: null,
    },
  ],
  dontRoundUnitPrice: 'eeb268ae-5cba-4a06-99cf-c44d4a88a6cc',
  agreement: '8deacab1-be54-4698-ba5d-a8bdcdea84b8',
  notes: '17ebaab6-e14c-453b-8e47-58d793c8db19',
  instructions: '56cea5a4-a730-4165-a359-61099b0c8378',
  useLocaleQuantityFormatting: '024572e9-4f98-4725-be75-7439983cae5b',
  approvalPurchase: '0c575ca3-3a89-400b-96a3-dd4347c9107c',
  approvalAudit: '778a21b8-9314-4613-a8b7-049d8a64af80',
  approvalApprove: '6d8ee469-a9c1-48f6-ad6a-1d04f0fdc707',
  approvalSeller: 'a8f89f53-43ac-4a56-b3f3-d99da228e69d',
  approvalSignatureSeal: 'e35b51e4-c458-498b-af28-77fd31de84df',
  approvalSignedBackDate: '25ce6acf-9feb-4572-a1af-5a4db357428f',
}
